import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { authAPI } from '../services/api';
import { apiCall } from '../services/realApi';
import { setToken, clearToken } from '../utils/auth';
import { demoModeManager } from '../services/demoModeManager';
import { mockUserAuth } from '../services/mockData';
import { IS_STATIC_DEMO } from '../services/config';
import { DEMO_USERS } from '../services/demoUsers';
import { startDemoNotificationSession, clearDemoNotificationSession } from '../services/demoNotifications';
import type { User, UserMode, LoginResult } from '../types';

interface AuthContextValue {
  user: User | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<{ success: boolean; error?: string }>;
  refreshAuth: () => Promise<void>;
  refreshAuthSilently: () => Promise<void>;
  setUserDirectly: (userData: User | null) => void;
  isAuthenticated: boolean;
  loading: boolean;
  authError: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const previousSubscriptionPlan = useRef<UserMode | null>(null);

  const getUserMode = (userData: User | null): UserMode => {
    if (!userData) return 'subscribed';
    if (userData.is_anonymous) return 'anonymous';

    const subscriptionPlan = userData.subscription_plan || userData.client?.subscription_plan;
    if (subscriptionPlan === 'assessment_only') return 'onboarding';

    return 'subscribed';
  };

  useEffect(() => {
    if (!user) {
      previousSubscriptionPlan.current = null;
      return;
    }

    const currentMode = getUserMode(user);
    const previousMode = previousSubscriptionPlan.current;

    if (
      (previousMode === 'onboarding' || previousMode === 'anonymous') &&
      currentMode === 'subscribed'
    ) {
      demoModeManager.clearMockData();
    }

    previousSubscriptionPlan.current = currentMode;
  }, [user]);

  const fetchUserAuthorization = useCallback(async (): Promise<User | null> => {
    if (IS_STATIC_DEMO) {
      const stored = localStorage.getItem('user');
      if (stored && stored !== 'undefined' && stored !== 'null') {
        try {
          return JSON.parse(stored);
        } catch {
          return null;
        }
      }
      return null;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return null;
      }

      const authData = await apiCall('/auth/me');
      if (authData) {
        const subscriptionPlan = authData.subscription_plan || authData.client?.subscription_plan;
        if (subscriptionPlan === 'assessment_only') {
          authData.permissions = mockUserAuth.permissions;
          authData.aws_accessible_modules = mockUserAuth.aws_accessible_modules;
        }
        localStorage.setItem('user', JSON.stringify(authData));
        return authData;
      }
      return null;
    } catch (error: unknown) {
      console.error('Error fetching user authorization:', error);
      if (error && typeof error === 'object' && 'status' in error && (error as { status: number }).status === 401) {
        clearToken();
        localStorage.removeItem('user');
      }
      return null;
    }
  }, []);

  const setUserDirectly = useCallback((userData: User | null) => {
    setUser(userData);
    setLoading(false);
  }, []);

  const refreshAuth = useCallback(async () => {
    if (IS_STATIC_DEMO) {
      setLoading(true);
      const stored = localStorage.getItem('user');
      if (stored && stored !== 'undefined' && stored !== 'null') {
        try {
          setUser(JSON.parse(stored));
        } catch {
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
      return;
    }

    setLoading(true);
    setAuthError(null);
    try {
      const anonymousSession = localStorage.getItem('anonymous_session');
      const token = localStorage.getItem('token');

      if (anonymousSession && token) {
        const sessionData = JSON.parse(anonymousSession);

        setUser({
          is_anonymous: true,
          client_name: sessionData.client_name,
          client_id: sessionData.client_id,
          subscription_plan: sessionData.subscription_plan,
          email: null,
          name: null,
          role: 'anonymous',
          permissions: mockUserAuth.permissions,
          aws_accessible_modules: mockUserAuth.aws_accessible_modules
        } as User);
        setLoading(false);
        return;
      }

      const authData = await fetchUserAuthorization();
      if (authData) {
        setUser(authData);
      } else {
        setUser(null);
      }
    } catch (error: unknown) {
      setAuthError(error instanceof Error ? error.message : 'Unknown error');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [fetchUserAuthorization]);

  const refreshAuthSilently = useCallback(async () => {
    if (IS_STATIC_DEMO) {
      const stored = localStorage.getItem('user');
      if (stored && stored !== 'undefined' && stored !== 'null') {
        try {
          setUser(JSON.parse(stored));
        } catch {
          setUser(null);
        }
      }
      return;
    }

    try {
      const anonymousSession = localStorage.getItem('anonymous_session');
      const token = localStorage.getItem('token');

      if (anonymousSession && token) {
        const sessionData = JSON.parse(anonymousSession);
        setUser({
          is_anonymous: true,
          client_name: sessionData.client_name,
          client_id: sessionData.client_id,
          subscription_plan: sessionData.subscription_plan,
          email: null,
          name: null,
          role: 'anonymous',
          permissions: mockUserAuth.permissions,
          aws_accessible_modules: mockUserAuth.aws_accessible_modules
        } as User);
        return;
      }

      const authData = await fetchUserAuthorization();
      if (authData) {
        setUser(authData);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('[AuthContext] refreshAuthSilently failed:', error);
      setUser(null);
    }
  }, [fetchUserAuthorization]);

  // Initialization effect
  useEffect(() => {
    if (IS_STATIC_DEMO) {
      const stored = localStorage.getItem('user');
      if (stored && stored !== 'undefined' && stored !== 'null') {
        try {
          setUser(JSON.parse(stored));
        } catch {
          // Invalid stored user
        }
      }
      setLoading(false);
      return;
    }

    const anonymousSession = localStorage.getItem('anonymous_session');
    const token = localStorage.getItem('token');

    if (anonymousSession && token) {
      try {
        const sessionData = JSON.parse(anonymousSession);

        setUser({
          is_anonymous: true,
          client_name: sessionData.client_name,
          client_id: sessionData.client_id,
          subscription_plan: sessionData.subscription_plan,
          email: null,
          name: null,
          role: 'anonymous',
          permissions: mockUserAuth.permissions,
          aws_accessible_modules: mockUserAuth.aws_accessible_modules
        } as User);
        setLoading(false);
      } catch (error) {
        console.error('[AuthContext] Failed to parse anonymous session:', error);
        localStorage.removeItem('anonymous_session');
        setLoading(false);
      }
      return;
    }

    const currentUser = authAPI.getCurrentUser();

    if (currentUser && token) {
      fetchUserAuthorization().then(authData => {
        if (authData) {
          setUser(authData);
        } else {
          const tokenStillExists = localStorage.getItem('token');
          if (tokenStillExists) {
            setUser(currentUser);
          } else {
            setUser(null);
          }
        }
        setLoading(false);
      }).catch(() => {
        const tokenStillExists = localStorage.getItem('token');
        if (tokenStillExists) {
          setUser(currentUser);
        } else {
          setUser(null);
        }
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [fetchUserAuthorization]);

  const login = async (email: string, password: string): Promise<LoginResult> => {
    if (IS_STATIC_DEMO) {
      try {
        setLoading(true);
        setAuthError(null);

        const demoUser = (DEMO_USERS as unknown as Record<string, User>)[email];
        if (demoUser && password === 'password') {
          setToken('demo-token-' + demoUser.role);
          localStorage.setItem('user', JSON.stringify(demoUser));
          setUser(demoUser);
          startDemoNotificationSession();
          setLoading(false);
          return { success: true };
        }

        setLoading(false);
        return { success: false, error: 'Invalid demo credentials. Use one of the demo users below.' };
      } catch {
        setLoading(false);
        return { success: false, error: 'Login failed' };
      }
    }

    try {
      setLoading(true);
      setAuthError(null);

      const result = await authAPI.login(email, password);
      if (result.success) {
        const authData = await fetchUserAuthorization();
        if (authData) {
          setUser(authData);
        } else {
          setUser(result.user);
        }
        setLoading(false);
        return { success: true };
      }
      setLoading(false);
      return { success: false, error: result.error };
    } catch (error: unknown) {
      setLoading(false);
      setAuthError(error instanceof Error ? error.message : 'Unknown error');
      return { success: false, error: 'Login failed' };
    }
  };

  const logout = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      await authAPI.logout();
      localStorage.removeItem('selectedClientId');
      localStorage.removeItem('anonymous_session');
      demoModeManager.clearMockData();
      clearDemoNotificationSession();
      setUser(null);
      setAuthError(null);
      return { success: true };
    } catch {
      return { success: false, error: 'Logout failed' };
    }
  };

  const value: AuthContextValue = {
    user,
    login,
    logout,
    refreshAuth,
    refreshAuthSilently,
    setUserDirectly,
    isAuthenticated: !!user,
    loading,
    authError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
